/* tslint:disable */
/* eslint-disable */

import * as runtime from '../runtime';
import { BaseAPI } from '../baseApi';
import { User } from '../types';
import type { Persona } from '@/personas/types';
import type { TimerDisplay } from '@/utils/timerDisplay';
import { HttpStatusCode } from 'axios';

export interface UpdateTimeZoneRequest {
  timeZone: string;
}

export class AuthApi extends BaseAPI {
  /**
   *
   * Operation for logging in to TMW
   */
  async getUser(): Promise<User | null> {
    try {
      const response = await this.request({
        path: `/user`,
        method: 'GET',
      });

      const res = await new runtime.JSONApiResponse(
        response,
        (jsonValue): User => jsonValue,
      ).value();
      return res;
    } catch (error) {
      // /user är appens AUTENTISERINGSPROB, inte en vanlig resurs. Den enda resurs
      // det finns behörighetsregler för här är kontot självt, och ett inloggat konto
      // får alltid läsa sitt eget — därför kan 403 på just den här sökvägen inte
      // betyda "inloggad men nekad". Det betyder att anropet inte var autentiserat.
      //
      // Det är också precis vad den GAMLA backenden svarar på ett oautentiserat
      // /user (den nya svarar 401). Frontend och backend är två artefakter som
      // uppdateras vid olika tidpunkter, och PWA-cachen garanterar att de inte är i
      // synk hos användarna. Båda koderna måste alltså leda till inloggningsskärmen.
      //
      // Alternativet — att kasta 403 vidare — gör att AppContext sätter
      // authenticated='error' och att varje användare med en kvarvarande
      // XSRF-TOKEN-kaka men utan giltig session fastnar i felskärmen i stället för
      // att få logga in. Regeln gäller BARA getUser; övriga endpoints behåller
      // 403 = "inloggad men får inte", som kontraktet säger.
      const status = error instanceof runtime.ResponseError ? error.response?.status : undefined;
      if (status === 401 || status === 403) {
        return null;
      }
      throw error;
    }
  }

  async deleteAccount(): Promise<HttpStatusCode | null> {
    try {
      const response = await this.request({
        path: `/user`,
        method: 'DELETE',
      });
      return response.status;
    } catch (e) {
      return null;
    }
  }

  async updateTimeZone(timeZone: string): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    const headerParameters: runtime.HTTPHeaders = {};
    headerParameters['Content-Type'] = 'application/json';

    try {
      const response = await this.request({
        path: `/user/time-zone`,
        method: 'PUT',
        query: queryParameters,
        headers: headerParameters,
        body: timeZone,
      });

      return response.status;
    } catch (error) {}
  }

  async updateVersion(version: string): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    const headerParameters: runtime.HTTPHeaders = {};
    headerParameters['Content-Type'] = 'application/json';

    try {
      const response = await this.request({
        path: `/user/version`,
        method: 'PUT',
        query: queryParameters,
        headers: headerParameters,
        body: version,
      });

      return response.status;
    } catch (error) {}
  }

  /**
   * Sätter en persona (BACKEND-KONTRAKT p.3). Med supervisedTag skriver HANDLEDAREN
   * värdet på brukarens konto (kopplingen verifieras i backend); utan tag skriver
   * kontot sitt eget — samma mönster som updateTimerDisplay.
   *
   * Backenden binder @RequestBody PersonaUpdate, så bodyn är objektet { persona }
   * (axios JSON-serialiserar → {"persona":"child"}). En rå sträng ger 400 (skickas
   * oserialiserad). Samma objekt-mönster som PersonaUpdate.
   */
  async updatePersona(persona: Persona, supervisedTag?: string): Promise<User> {
    const response = await this.request({
      path: `/user/persona`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      query: supervisedTag !== undefined ? { supervisedTag } : {},
      body: { persona },
    });
    return new runtime.JSONApiResponse(response, (jsonValue): User => jsonValue).value();
  }

  /**
   * Sätter aktivitetsklockans form OCH om siffrorna visas. Med supervisedTag skriver
   * HANDLEDAREN värdet på
   * brukarens konto (kopplingen verifieras i backend); utan tag skriver kontot sitt
   * eget. Svaret är den uppdaterade användaren, så anroparen slipper gissa utfallet.
   */
  async updateTimerDisplay(display: TimerDisplay, supervisedTag?: string): Promise<User> {
    const response = await this.request({
      path: `/user/timer-display`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      query: supervisedTag !== undefined ? { supervisedTag } : {},
      body: { timerDisplayShape: display.shape, timerDisplayDigits: display.digits },
    });
    return new runtime.JSONApiResponse(response, (jsonValue): User => jsonValue).value();
  }

  /**
   * Sätter om rutinens legobitar ska visa stegens namn. Med supervisedTag skriver
   * HANDLEDAREN värdet på brukarens konto (kopplingen verifieras i backend); utan tag
   * skriver kontot sitt eget. Samma mönster som updateTimerDisplay.
   */
  async updateRoutineStepLabels(routineStepLabels: boolean, supervisedTag?: string): Promise<User> {
    const response = await this.request({
      path: `/user/routine-step-labels`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      query: supervisedTag !== undefined ? { supervisedTag } : {},
      body: { routineStepLabels },
    });
    return new runtime.JSONApiResponse(response, (jsonValue): User => jsonValue).value();
  }

  /**
   *
   * Log out of your account
   */
  async logout(): Promise<HttpStatusCode | undefined> {
    try {
      const response = await this.request({
        path: `/logout`,
        method: 'POST',
      });

      return response.status;
    } catch (error) {}
  }
}
